import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { AutoBackupList } from "@/components/AutoBackupList";
import { DatabaseBackup, Download, Upload, Loader2 } from "lucide-react";

export const BackupSection = () => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const exportAll = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/backup");
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([JSON.stringify(data)], { type: "application/json" }));
      a.download = `sauvegarde-bandeaux-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      toast.success(`Sauvegarde téléchargée : ${data.banners.length} bandeau(x), ${data.posters.length} affiche(s), ${data.club_logos.length} logo(s)`);
    } catch {
      toast.error("Échec de la sauvegarde");
    } finally {
      setBusy(false);
    }
  };

  const restore = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const data = JSON.parse(await file.text());
      const { data: res } = await api.post("/restore", { data });
      const r = res.restored;
      toast.success(`Restauré : ${r.banners} bandeau(x), ${r.posters} affiche(s), ${r.club_logos} logo(s)`);
    } catch {
      toast.error("Fichier de sauvegarde invalide ou restauration impossible");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <section className="rounded-2xl border border-border bg-card p-6" data-testid="backup-section">
      <div className="mb-4 flex items-center gap-2">
        <DatabaseBackup className="h-5 w-5 text-accent" />
        <h2 className="font-display text-lg font-semibold">Sauvegarde / Restauration</h2>
      </div>
      <p className="mb-4 text-sm text-muted-foreground">
        Téléchargez tous vos bandeaux, affiches, modèles et logos dans un fichier, puis restaurez-le sur une autre version de l'application
        (ex. de la prévisualisation vers angodel.be). La restauration ajoute ou remplace les éléments du même identifiant, sans rien supprimer.
      </p>
      <div className="flex flex-wrap gap-3">
        <Button onClick={exportAll} disabled={busy} data-testid="backup-export-button">
          {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Download className="mr-1 h-4 w-4" />} Télécharger la sauvegarde
        </Button>
        <Button variant="secondary" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="backup-restore-button">
          <Upload className="mr-1 h-4 w-4" /> Restaurer une sauvegarde
        </Button>
        <input ref={fileRef} type="file" accept="application/json,.json" className="hidden" data-testid="backup-file-input"
          onChange={(e) => restore(e.target.files?.[0])} />
      </div>
      <AutoBackupList />
    </section>
  );
};
